import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Rss, SearchCheck } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import {
  DataTable,
  useDataTable,
  useSearchListState,
  type SearchPatch,
} from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import type { SeoIssue } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';

import { IndexNowCard, useStoreDetail } from '@/features/stores';
import { useStore } from '@/shared/store/store-context';

import { SeoAuditsCard } from '../components/seo-audits-card';
import { useSeoIssueColumns } from '../components/seo-issue-columns';
import { seoIssueFilters } from '../components/seo-issue-filters';
import { useSeoIssues } from '../hooks/use-seo-issues';
import { useResolveSeoIssue } from '../hooks/use-seo-mutations';

const ROUTE_ID = '/_authed/_active/s/$storeId/content/seo' as const;
const ROUTE_PATH = '/s/$storeId/content/seo' as const;

function SeoIssues() {
  const { t } = useTranslation('seo');
  const store = useStore();
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });
  const resolve = useResolveSeoIssue(store.id);
  // IndexNow lives with the other search-engine settings; the key is on the full store record.
  const detail = useStoreDetail(store.id);

  const filters = useMemo(() => seoIssueFilters(t), [t]);
  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const list = useSearchListState({ search, onSearchChange, filters });
  const query = useSeoIssues(store.id, list.params);

  const mutateResolve = resolve.mutate;
  const onResolve = useCallback((issue: SeoIssue) => mutateResolve(issue.id), [mutateResolve]);
  const columns = useSeoIssueColumns({
    storeId: store.id,
    resolvingId: resolve.isPending ? resolve.variables : null,
    onResolve,
  });
  const table = useDataTable({
    mode: 'server',
    data: query.data?.items ?? [],
    nextCursor: query.data?.nextCursor,
    list,
    columns,
    filters,
    getRowId: (row) => row.id,
    tableId: 'seo-issues',
  });

  const narrowed = Object.keys(list.filters).length > 0 || Boolean(list.cursor);
  const isEmptyList = query.isSuccess && query.data.items.length === 0 && !narrowed;

  return (
    <PageContent>
      <PageHeader title={t('title')} description={t('description', { store: store.name })} />
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex min-w-0 flex-col">
          {isEmptyList ? (
            <EmptyState
              icon={SearchCheck}
              title={t('empty.title')}
              description={t('empty.description')}
              tour="content.seo.empty"
              tourLink
            />
          ) : (
            <DataTable
              table={table}
              tour={{ table: 'content.seo.issues-table' }}
              search={false}
              loading={query.isPending}
              fetching={query.isFetching || resolve.isPending}
              error={query.error}
              onRetry={() => void query.refetch()}
              getRowLabel={(row) => t(`code.${row.code}`)}
              resourceName={{ singular: t('resource.one'), plural: t('resource.other') }}
              emptyState={{ title: t('empty.filtered') }}
            />
          )}
        </div>
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-1" data-tour="content.seo.audits">
            <SeoAuditsCard storeId={store.id} />
          </div>
          {detail.data ? (
            <div className="grid grid-cols-1" data-tour="content.seo.indexnow">
              <IndexNowCard store={detail.data} />
            </div>
          ) : null}
          <Card data-tour="content.seo.feeds-link">
            <CardHeader>
              <CardTitle className="text-[15px]">{t('feeds.title')}</CardTitle>
              <CardDescription>{t('feeds.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild variant="outline" size="sm">
                <Link to="/s/$storeId/settings/feeds" params={{ storeId: store.id }}>
                  <Rss aria-hidden />
                  {t('feeds.open')}
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </PageContent>
  );
}

export function SeoIssuesPage() {
  return (
    <FeatureErrorBoundary name="seo.issues">
      <SeoIssues />
    </FeatureErrorBoundary>
  );
}
